import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { flyInOut } from '../../libs/animations/fly-animations';
import { ModalHelper } from '../../libs/component-lib/components/modal/navigator/modal-helper';
import { presentationOptions } from '../../libs/component-lib/components/modal/presenter/modal-presenter.config';
import { ModalPresenter } from '../../libs/component-lib/components/modal/presenter/modal-presenter.service';
import { ChargingCardInputComponent } from '../../libs/feature-lib/charging-card/charging-card-input/charging-card-input.component';
import { ChargingStationInputComponent } from '../../libs/feature-lib/charging-station/input/input.component';
import { ChargingIntroductionComponent } from '../../libs/feature-lib/introduction/introduction.component';
import { IntroductionType } from '../../libs/feature-lib/introduction/introduction.model';
import { AnalyticsHelperService } from '../../services/analytics-helper.service';
import { SmallBizStoreService } from '../../stores/small-biz-store.service';
import { ButtonSizeEnum } from './../../libs/component-lib/enums/button-size.enum';
import { EvsbProductOverviewModel } from './evsb-product-overview.model';
import { EvsbProductOverviewService } from './evsb-product-overview.service';

@Component({
  selector: 'app-evsb-product-overview',
  templateUrl: './evsb-product-overview.component.html',
  styleUrls: ['./evsb-product-overview.component.scss'],
  animations: [flyInOut],
})
export class EvsbProductOverviewComponent implements OnInit, OnDestroy {
  @ViewChild('greenEnergyInfoModal')
  greenEnergyInfoModal: TemplateRef<void>;

  public model$: Observable<EvsbProductOverviewModel>;
  public btnSmall = ButtonSizeEnum.small;

  private notifier = new Subject<void>();

  constructor(
    private title: Title,
    private translate: TranslateService,
    private modalService: ModalPresenter,
    private pageService: EvsbProductOverviewService,
    private store: SmallBizStoreService,
    private router: Router,
    private modalHelper: ModalHelper,
    private analyticsHelperService: AnalyticsHelperService
  ) {
    this.translate.onLangChange.pipe(takeUntil(this.notifier)).subscribe(() => {
      this.title.setTitle(this.translate.instant('modules.evsb.pages.title.productOverview'));
    });
  }

  ngOnInit(): void {
    this.model$ = this.pageService.getModel$();
    this.analytics();
  }

  ngOnDestroy(): void {
    this.notifier.next();
    this.notifier.complete();
  }

  // Green Energy
  //
  onSimulateGreenEnergy() {
    this.pageService.analyticsCta('click-cta-overview-screen', 'green-energy-simulator');
    this.model$.pipe(take(1)).subscribe((model) => {
      window.open(model.greenEnergySimulatorUrl, '_blank').focus();
    });
  }

  openGreenEnergyInfoModal() {
    this.modalService.openCentered(this.greenEnergyInfoModal);
    this.pageService.analyticsGreenEnergyPopup();
  }

  // Charge Cards
  //
  onAddChargeCard() {
    this.startChargeCardModal(false);
    this.pageService.analyticsCta('select-product', 'charging card');
  }

  deleteChargeCards() {
    this.store.setChargeCards(0);
    this.pageService.analyticsCta('delete-product', 'charging card');
  }

  editChargeCards() {
    this.startChargeCardModal(true);
    this.pageService.analyticsCta('modify-product', 'charging card');
  }

  startChargeCardModal(editing = false) {
    if (editing) {
      const modalInstance = this.modalHelper.openMultiViewModal(ChargingCardInputComponent, presentationOptions);
      return;
    }

    const modalInstance = this.modalHelper.openMultiViewModal(ChargingIntroductionComponent, presentationOptions);
    modalInstance.data = { introductionType: IntroductionType.skipIntroChargingCard };
    this.analyticsHelperService.analyticsWorkflowStep('overlay-charging-card');
  }

  // Charge stations
  //
  onAddChargePoint() {
    this.startChargeStationModal(false);
    this.pageService.analyticsCta('select-product', 'charging station');
  }

  deleteChargePoints() {
    this.store.setChargingStations(0);
    this.pageService.analyticsCta('delete-product', 'charging station');
  }

  editChargePoints() {
    this.startChargeStationModal(true);
    this.pageService.analyticsCta('modify-product', 'charging station');
  }

  startChargeStationModal(editing = false) {
    if (editing) {
      const modalInstance = this.modalHelper.openMultiViewModal(ChargingStationInputComponent, presentationOptions);
      return;
    }
    const modalInstance = this.modalHelper.openMultiViewModal(ChargingIntroductionComponent, presentationOptions);
    modalInstance.data = { introductionType: IntroductionType.skipIntroChargingStation };
    this.analyticsHelperService.analyticsWorkflowStep('overlay-charging-station');
  }

  public onRequestFinalInvoice() {
    this.model$.pipe(take(1)).subscribe((model) => {
      if (model.canRequestFinalInvoice) {
        this.router.navigate(['/final-invoice'], { skipLocationChange: true });
        this.pageService.analyticsCta('quote-request', 'submit');
      }
    });
  }

  private analytics() {
    this.model$.pipe(take(1)).subscribe((model) => {
      // if we cannot request invoice, we have no products yet
      if (!model.canRequestFinalInvoice) {
        this.pageService.analyticsEmpty();
      }

      if (model.chargeCard || model.chargePoint) {
        this.analyticsHelperService.analyticsWorkflowStep('overview/product-added');
      }
    });
  }
}
